import { useDroppable } from '@dnd-kit/core';
import type { CSSProperties } from 'react';

import type { PipelineColumn as PipelineColumnDto } from '@/pages/pipelines/common/types';

import { getPipelineStageDisplayLabel } from './stage-presentation';
import {
  CompactColumnContent,
  CompactColumnDropZone,
  CompactColumnLabel,
  CompactColumnShell,
  CompactCountBadge,
} from './styled';

type Props = {
  column: PipelineColumnDto;
  stageStyle: CSSProperties;
  dragAvailable: boolean;
  dragInteractionDisabled: boolean;
  dragSourceStage?: string | null;
  onExpand?: (_stage: string) => void;
};

export const PipelineCompactColumn = ({
  column,
  stageStyle,
  dragAvailable,
  dragInteractionDisabled,
  dragSourceStage,
  onExpand,
}: Props) => {
  const isDragSource = dragSourceStage === column.stage;
  const { setNodeRef, isOver } = useDroppable({
    id: column.stage,
    disabled: !dragAvailable || dragInteractionDisabled || isDragSource,
  });

  const compactColumn = (
    <CompactColumnShell
      type="button"
      aria-label={`Open ${column.label}`}
      data-testid={`pipeline-column-compact-${column.stage}`}
      data-terminal={column.terminal}
      style={stageStyle}
      onClick={() => onExpand?.(column.stage)}
    >
      <CompactColumnContent>
        <CompactColumnLabel>
          {getPipelineStageDisplayLabel(column.compactLabel ?? column.label, true)}
        </CompactColumnLabel>
      </CompactColumnContent>
      <CompactCountBadge>{column.count}</CompactCountBadge>
    </CompactColumnShell>
  );

  if (!dragAvailable) return compactColumn;

  return (
    <CompactColumnDropZone
      ref={setNodeRef}
      data-testid={`pipeline-column-compact-dropzone-${column.stage}`}
      data-dragging-over={!isDragSource && isOver}
    >
      {compactColumn}
    </CompactColumnDropZone>
  );
};
